import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { graphql, useStaticQuery } from "gatsby"
import { RichText } from "prismic-dom"
import css from "@styled-system/css"

import {
  Accordion,
  AspectRatio,
  Box,
  Column,
  Columns,
  Divider,
  FluidText,
  Footer,
  Heading,
  Image,
  PageHeader,
  Paragraph,
  SEO,
  VStack,
  Wrapper,
} from "components"

import { HeaderFade } from "animations"

function linkResolver(doc) {
  return "/"
}

function AboutPage({ preview }) {
  const { page } = useStaticQuery(
    graphql`
      {
        page: prismicAbout {
          dataString
        }
      }
    `
  )
  const [aboutPage, setAboutPage] = useState(() => {
    if (page.dataString) {
      let dataParsed = JSON.parse(page.dataString)
      return {
        seo: {
          title: dataParsed.seo_title && dataParsed.seo_title[0].text,
          description:
            dataParsed.seo_description && dataParsed.seo_description[0].text,
          image: dataParsed.seo_image && dataParsed.seo_image.url,
          imageAlt: dataParsed.seo_image && dataParsed.seo_image.alt,
        },
        title: dataParsed.title && dataParsed.title[0].text,
        subtitle:
          dataParsed.subtitle &&
          RichText.asHtml(dataParsed.subtitle, linkResolver),
        calloutTitle:
          dataParsed.callout_title && dataParsed.callout_title[0].text,
        calloutDescription:
          dataParsed.callout_description &&
          RichText.asHtml(dataParsed.callout_description, linkResolver),
        foundationTitle:
          dataParsed.foundation_title && dataParsed.foundation_title[0].text,
        foundationImage: dataParsed.foundation_image,
        foundationDescription:
          dataParsed.foundation_description &&
          RichText.asHtml(dataParsed.foundation_description, linkResolver),
        principles: dataParsed.principles,
        services: dataParsed.services,
        clients: dataParsed.clients,
        team: dataParsed.team,
      }
    }
  })

  useEffect(() => {
    // Parse and create page data based on either static query or preview API
    if (preview) {
      setAboutPage({
        seo: {
          title: preview.data.seo_title && preview.data.seo_title[0].text,
          description:
            preview.data.seo_description &&
            preview.data.seo_description[0].text,
          image: preview.data.seo_image && preview.data.seo_image.url,
          imageAlt: preview.data.seo_image && preview.data.seo_image.alt,
        },
        title: preview.data.title && preview.data.title[0].text,
        subtitle:
          preview.data.subtitle &&
          RichText.asHtml(preview.data.subtitle, linkResolver),
        calloutTitle: preview.callout_title && preview.callout_title[0].text,
        calloutDescription:
          preview.callout_description &&
          RichText.asHtml(preview.callout_description, linkResolver),
        foundationTitle:
          preview.foundation_title && preview.foundation_title[0].text,
        foundationImage: preview.foundation_image,
        foundationDescription:
          preview.foundation_description &&
          RichText.asHtml(preview.foundation_description, linkResolver),
        principles: preview.data.principles,
        services: preview.data.services,
        clients: preview.data.clients,
        team: preview.data.team,
      })
    }
  }, [])

  return (
    <>
      {aboutPage && aboutPage.seo && (
        <SEO
          title={aboutPage.seo && aboutPage.seo.title}
          description={aboutPage.seo && aboutPage.seo.description}
          image={aboutPage.seo && aboutPage.seo.image}
          imageAlt={aboutPage.seo && aboutPage.seo.imageAlt}
        />
      )}

      <PageHeader />

      <Wrapper pt={["layout.6", "layout.7"]}>
        {/* Heading */}
        <motion.div
          variants={HeaderFade(0.33)}
          initial="initial"
          animate="visible"
          exit="exit"
          key={"aboutHeroText"}
        >
          <FluidText size={900}>{aboutPage.title}</FluidText>
        </motion.div>
      </Wrapper>

      <motion.div
        variants={HeaderFade(0.7)}
        initial="initial"
        animate="visible"
        exit="exit"
        key={"aboutBody"}
      >
        <Wrapper py={["layout.6", "layout.7"]}>
          <VStack space={["layout.6", "layout.7"]}>
            {/* Tagline */}
            <Heading
              size={500}
              as="div"
              dangerouslySetInnerHTML={{ __html: aboutPage.subtitle }}
            />

            {/* Callout */}
            {aboutPage.calloutTitle && aboutPage.calloutDescription && (
              <VStack space="layout.4">
                <Divider />
                <Columns space={["layout.4", "layout.4", "layout.6"]}>
                  <Column width={[1 / 1, 1 / 3]}>
                    <Heading size={500}>{aboutPage.calloutTitle}</Heading>
                  </Column>
                  <Column width={[1 / 1, 2 / 3]}>
                    <Paragraph
                      size={500}
                      as="div"
                      dangerouslySetInnerHTML={{
                        __html: aboutPage.calloutDescription,
                      }}
                      css={{
                        a: {
                          fontWeight: "bold",
                        },
                        p: {
                          "&:not(:last-of-type)": {
                            marginBottom: "1em",
                          },
                        },
                      }}
                    />
                  </Column>
                </Columns>
              </VStack>
            )}

            {/* Info */}
            {aboutPage.principles && aboutPage.principles[0] && (
              <Columns space={["layout.4", "layout.4", "layout.6"]}>
                {aboutPage.principles &&
                  aboutPage.principles.map((principle, index) => (
                    <Column width={[1 / 1, 1 / 3]} key={"principle" + index}>
                      <VStack space="layout.4">
                        <Divider />
                        <Paragraph
                          size={500}
                          as="div"
                          dangerouslySetInnerHTML={{
                            __html:
                              principle.principle_item &&
                              RichText.asHtml(
                                principle.principle_item,
                                linkResolver
                              ),
                          }}
                          css={css({
                            strong: {
                              mt: "-0.2em",
                              fontFamily: "sans",
                              fontWeight: 700,
                            },
                          })}
                        />
                      </VStack>
                    </Column>
                  ))}
              </Columns>
            )}

            {/* Services */}
            {aboutPage.services && aboutPage.services[0] && (
              <VStack space="layout.4">
                <Divider />
                <Columns space={["layout.4", "layout.4", "layout.6"]}>
                  <Column width={[1 / 1, 1 / 3]}>
                    <Heading size={500}>Services</Heading>
                  </Column>
                  <Column width={[1 / 1, 2 / 3]}>
                    <VStack space="layout.4">
                      <Accordion
                        items={
                          aboutPage.services &&
                          aboutPage.services.map(service => ({
                            label: service.service_type,
                            children: (
                              <Paragraph
                                size={500}
                                as="div"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    service.service_subtypes &&
                                    RichText.asHtml(
                                      service.service_subtypes,
                                      linkResolver
                                    ),
                                }}
                                css={{
                                  p: {
                                    "&:not(:last-of-type)": {
                                      marginBottom: "1em",
                                    },
                                  },
                                }}
                              />
                            ),
                          }))
                        }
                      />
                    </VStack>
                  </Column>
                </Columns>
              </VStack>
            )}

            {/* Clients */}
            {aboutPage.clients && aboutPage.clients[0] && (
              <VStack space="layout.4">
                <Divider />
                <Columns space={["layout.4", "layout.4", "layout.6"]}>
                  <Column width={[1 / 1, 1 / 3]}>
                    <Heading size={500}>Clients</Heading>
                  </Column>
                  <Column width={[1 / 1, 2 / 3]}>
                    <Paragraph size={500}>
                      {aboutPage.clients &&
                        aboutPage.clients.map((client, index) => (
                          <Box as="span" key={"client" + index}>
                            {client.client_name + ". "}
                          </Box>
                        ))}
                    </Paragraph>
                  </Column>
                </Columns>
              </VStack>
            )}

            {/* Team */}
            {aboutPage.team &&
              aboutPage.team.length > 0 &&
              aboutPage.team[0].name && (
                <VStack space="layout.4">
                  <Divider />
                  <Columns space={["layout.4", "layout.4", "layout.6"]}>
                    <Column width={[1 / 1, 1 / 3]}>
                      <Heading size={500}>Team</Heading>
                    </Column>
                    <Column width={[1 / 1, 2 / 3]}>
                      <Columns space={["layout.4", "layout.4", "layout.6"]}>
                        {aboutPage.team &&
                          aboutPage.team.map((team, index) => (
                            <Column
                              width={[1 / 1, 1 / 1, 1 / 2]}
                              key={"teamMember" + index}
                            >
                              <AspectRatio ratio="3:4" bg="text">
                                <Box
                                  as="img"
                                  src={team.headshot && team.headshot.url}
                                  width="100%"
                                  height="100%"
                                  css={{ objectFit: "cover" }}
                                />
                              </AspectRatio>
                              <Paragraph size={500} mt="layout.2" pb="layout.1">
                                {team.name}
                                {team.role && ", " + team.role}
                              </Paragraph>
                              <Box
                                as="p"
                                width={["100%", "calc(50% - 16px)"]}
                                m={0}
                                py={[0, null, 1]}
                              >
                                {team.email && (
                                  <Box
                                    as="a"
                                    href={"mailto:" + team.email}
                                    target="_blank"
                                    rel="nopener noreferrer"
                                    display="block"
                                  >
                                    {team.email}
                                  </Box>
                                )}
                                {team.phone && (
                                  <Box
                                    as="a"
                                    href={"tel:" + team.phone}
                                    target="_blank"
                                    rel="nopener noreferrer"
                                    display="block"
                                  >
                                    {team.phone}
                                  </Box>
                                )}
                              </Box>
                            </Column>
                          ))}
                      </Columns>
                    </Column>
                  </Columns>
                </VStack>
              )}
            {/* Foundation */}
            {aboutPage.foundationTitle && aboutPage.foundationDescription && (
              <VStack space="layout.4">
                <Divider />
                {aboutPage.foundationImage && aboutPage.foundationImage.url && (
                  <AspectRatio
                    ratio={
                      aboutPage.foundationImage.dimensions.width +
                      ":" +
                      aboutPage.foundationImage.dimensions.height
                    }
                    bg="background"
                    position="relative"
                  >
                    <Image
                      image={{
                        url: aboutPage.foundationImage.url + "&fit=fill&w=1600",
                        dimensions: {
                          height: aboutPage.foundationImage.dimensions.height,
                          width: aboutPage.foundationImage.dimensions.width,
                        },
                        half: {
                          url:
                            aboutPage.foundationImage.url + "&fit=fill&w=800",
                          dimensions: {
                            height:
                              aboutPage.foundationImage.dimensions.height / 7,
                            width:
                              aboutPage.foundationImage.dimensions.width / 7,
                          },
                        },
                        placeholder: {
                          url: aboutPage.foundationImage.url + "&fit=fill&w=16",
                          dimensions: {
                            height:
                              aboutPage.foundationImage.dimensions.height / 10,
                            width:
                              aboutPage.foundationImage.dimensions.width / 10,
                          },
                        },
                      }}
                      width="100%"
                      height="100%"
                      sizes="100vw"
                    />
                  </AspectRatio>
                )}
                <Columns space={["layout.4", "layout.4", "layout.6"]}>
                  <Column width={[1 / 1, 1 / 3]}>
                    <Heading size={500}>{aboutPage.foundationTitle}</Heading>
                  </Column>
                  <Column width={[1 / 1, 2 / 3]}>
                    <Paragraph
                      size={500}
                      as="div"
                      dangerouslySetInnerHTML={{
                        __html: aboutPage.foundationDescription,
                      }}
                      css={{
                        a: {
                          fontWeight: "bold",
                        },
                        p: {
                          "&:not(:last-of-type)": {
                            marginBottom: "1em",
                          },
                        },
                      }}
                    />
                  </Column>
                </Columns>
              </VStack>
            )}
          </VStack>
        </Wrapper>
      </motion.div>

      <Footer />
    </>
  )
}

export default AboutPage
